import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * State der Split-View-Seitenleiste (Tabs, aktiver Tab, Offen-Status,
 * Breite). Bewusst KEIN Routing — Tabs sind Session-UI; nur die Breite
 * wird in localStorage persistiert. Der Provider hängt an Layout und ist
 damit über alle Routen mount-stabil.
 */

export interface PanelTab {
  /** Stabiler Dedupe-Key (View + Identität), z. B. `contact:5`. */
  id: string;
  /** Key in PANEL_VIEWS (registry.tsx). */
  view: string;
  params?: Record<string, unknown>;
  /** Deutsch, bei open() berechnet; Re-open mit besserem Namen aktualisiert ihn. */
  title: string;
}

export interface PanelOpenMeta {
  tabKey: string;
  title: string;
}

export interface SidePanelContextValue {
  tabs: PanelTab[];
  activeId: string | null;
  isOpen: boolean;
  width: number;
  open: (view: string, params: Record<string, unknown> | undefined, meta: PanelOpenMeta) => void;
  closeTab: (id: string) => void;
  setActive: (id: string) => void;
  closePanel: () => void;
  toggle: () => void;
  setWidth: (w: number) => void;
}

const MAX_TABS = 8;
const MIN_WIDTH = 360;
const WIDTH_STORAGE_KEY = 'split_panel_width_v1';

const clampWidth = (w: number): number => {
  const max = Math.max(MIN_WIDTH, Math.floor(window.innerWidth * 0.8));
  return Math.min(Math.max(Math.round(w), MIN_WIDTH), max);
};

const defaultWidth = (): number =>
  clampWidth(Math.min(Math.floor(window.innerWidth * 0.5), 720));

const SidePanelContext = createContext<SidePanelContextValue | null>(null);

export function SidePanelProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<PanelTab[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [width, setWidthState] = useState<number>(() => {
    try {
      const saved = Number(window.localStorage.getItem(WIDTH_STORAGE_KEY));
      if (Number.isFinite(saved) && saved > 0) return clampWidth(saved);
    } catch {
      // localStorage nicht verfügbar (Privatfenster etc.) — Fallback unten.
    }
    return defaultWidth();
  });

  const open = useCallback(
    (view: string, params: Record<string, unknown> | undefined, meta: PanelOpenMeta) => {
      setTabs((prev) => {
        const existing = prev.find((t) => t.id === meta.tabKey);
        if (existing) {
          // Erneut geöffnet → Fokus + Titel/Params auffrischen (kein Duplikat).
          return prev.map((t) =>
            t.id === meta.tabKey ? { ...t, title: meta.title, params: params ?? t.params } : t
          );
        }
        const next = [...prev, { id: meta.tabKey, view, params, title: meta.title }];
        return next.length > MAX_TABS ? next.slice(next.length - MAX_TABS) : next;
      });
      setActiveId(meta.tabKey);
      setIsOpen(true);
    },
    []
  );

  const closeTab = useCallback(
    (id: string) => {
      const index = tabs.findIndex((t) => t.id === id);
      if (index === -1) return;
      const next = tabs.filter((t) => t.id !== id);
      setTabs(next);
      if (next.length === 0) {
        setIsOpen(false);
        setActiveId(null);
      } else if (id === activeId) {
        setActiveId(next[Math.min(index, next.length - 1)].id);
      }
    },
    [tabs, activeId]
  );

  const setActive = useCallback((id: string) => setActiveId(id), []);

  const closePanel = useCallback(() => setIsOpen(false), []);

  const toggle = useCallback(() => {
    setIsOpen((open) => !open);
  }, []);

  const setWidth = useCallback((w: number) => {
    const clamped = clampWidth(w);
    setWidthState(clamped);
    try {
      window.localStorage.setItem(WIDTH_STORAGE_KEY, String(clamped));
    } catch {
      // Persistenz ist Convenience — ohne localStorage läuft die Leiste trotzdem.
    }
  }, []);

  const value = useMemo(
    () => ({ tabs, activeId, isOpen, width, open, closeTab, setActive, closePanel, toggle, setWidth }),
    [tabs, activeId, isOpen, width, open, closeTab, setActive, closePanel, toggle, setWidth]
  );

  return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
}

export function useSidePanel(): SidePanelContextValue {
  const ctx = useContext(SidePanelContext);
  if (!ctx) {
    throw new Error('useSidePanel muss innerhalb von SidePanelProvider verwendet werden');
  }
  return ctx;
}